/**
 * Light, dark, or whatever the machine says.
 *
 * The whole mechanism is one attribute on <html>: `tokens.css` declares every colour with
 * `light-dark()`, so setting `data-theme` flips `color-scheme` and the palette follows —
 * along with the browser's own scrollbars, form controls and autofill, which a
 * class-based theme leaves behind in the wrong colours.
 *
 * The stored value is read again in `app.html`, before the bundle loads. Doing it only
 * here would render the OS theme first and correct it a moment later, which is the flash
 * of the wrong colour that every theme switch is judged by.
 */

import { browser } from '$app/environment';

export type Theme = 'system' | 'light' | 'dark';

export const THEMES: Theme[] = ['system', 'light', 'dark'];

/** Kept in step with the inline script in app.html. */
const KEY = 'sla-theme';

const isTheme = (value: unknown): value is Theme =>
	value === 'system' || value === 'light' || value === 'dark';

export function stored(): Theme {
	if (!browser) return 'system';
	try {
		const value = localStorage.getItem(KEY);
		return isTheme(value) ? value : 'system';
	} catch {
		// Private mode, or storage disabled entirely. The OS setting is a fine answer.
		return 'system';
	}
}

export function apply(theme: Theme) {
	if (!browser) return;

	// Removed rather than set to "system": the absence of the attribute is what lets the
	// `color-scheme: light dark` on :root follow the OS.
	if (theme === 'system') {
		delete document.documentElement.dataset.theme;
	} else {
		document.documentElement.dataset.theme = theme;
	}

	try {
		if (theme === 'system') localStorage.removeItem(KEY);
		else localStorage.setItem(KEY, theme);
	} catch {
		// The theme still applies for this tab; it just will not be remembered.
	}
}

export const themeLabel = (theme: Theme) =>
	theme === 'system' ? 'Match system' : theme === 'light' ? 'Light' : 'Dark';

/**
 * The next theme in the cycle, wrapping. Spelled out rather than indexed off `THEMES` so it
 * is total — an index lookup is `Theme | undefined` under `noUncheckedIndexedAccess`, and
 * widening the return type to satisfy that would be worse than repeating three names. Keep
 * the order here in step with `THEMES`.
 */
export const nextTheme = (theme: Theme): Theme =>
	theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
